import './reset.css';
import './index.css';

import { initialState, reducer } from 'app/reducer';
import AskAI from 'components/AskAI';
import classNames from 'classnames';
import Cursor from 'components/Cursor';
import EnterTransition from 'components/EnterTransition';
import Grain from 'components/Grain';
import Navbar from 'components/Navbar';
import ThemeProvider from 'components/ThemeProvider';
import { tokens } from 'components/ThemeProvider/theme';
import VisuallyHidden from 'components/VisuallyHidden';
import { Fragment, Suspense, createContext, lazy, useEffect, useReducer } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import { msToNum } from 'utils/style';
import { reflow } from 'utils/transition';
import ReactGA from "react-ga";

const Home = lazy(() => import('pages/Home'));
const Contact = lazy(() => import('pages/Contact'));
// const Articles = lazy(() => import('pages/Articles'));
const Page404 = lazy(() => import('pages/404'));
const Uses = lazy(() => import('pages/Uses'));

export const AppContext = createContext();
export const TransitionContext = createContext();

const TRACKING_ID = "UA-195127165-1";
ReactGA.initialize(TRACKING_ID);

const App = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  return (
    <AppContext.Provider value={{ ...state, dispatch }}>
      <ThemeProvider themeId="light">
        {/* Feature 4: gallery curtain — above everything, unmounts after lift */}
        <EnterTransition />
        <Grain />
        {/* Feature 2: custom cursor — pointer-events:none, aria-hidden */}
        <Cursor />
        {/* Ask my portfolio — front-end-only scripted assistant demo */}
        <AskAI />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </AppContext.Provider>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  return (
    <Fragment>
      <Helmet>
        <link rel="canonical" href={`http://myportfolio-sebastien.s3-website.eu-west-3.amazonaws.com/${pathname}`} />
      </Helmet>
      <VisuallyHidden showOnFocus as="a" className="skip-to-main" href="#MainContent">
        Skip to main content
      </VisuallyHidden>
      <Navbar location={location} />
      <TransitionGroup component="main" className="app" tabIndex={-1} id="MainContent">
        <Transition
          key={pathname}
          timeout={msToNum(tokens.base.durationS)}
          onEnter={reflow}
        >
          {status => (
            <TransitionContext.Provider value={{ status }}>
              <div className={classNames('app__page', `app__page--${status}`)}>
                <Suspense fallback={<Fragment />}>
                  <Routes location={location} key={pathname}>
                    <Route path="/" element={<Home />} />
                    <Route path="/contact" element={<Contact />} />
                    {/* <Route path="/articles" element={<Articles} /> /> */}
                    <Route path="/uses" element={<Uses />} />
                    <Route path="*" element={<Page404 />} />
                  </Routes>
                </Suspense>
              </div>
            </TransitionContext.Provider>
          )}
        </Transition>
      </TransitionGroup>
    </Fragment>
  );
};

export default App;
